import {
  WSElement,
  WSList,
  WSPage,
  WSPageToolbar,
  WSPageToolbarAction
} from "@wingspanhq/fe-component-library";
import { FrequencyAndScheduleStatus } from "@wingspanhq/payments/dist/interfaces";
import { useMemo } from "react";
import { Route, Switch, useHistory } from "react-router-dom";
import { INVOICING_PATH } from "../..";
import { KEYBOARD_SHORTCUT_SEARCH } from "../../../../constants/keyboardShortcuts";
import { useUserId } from "../../../../query/hooks/helpers";
import { useFeatureFlags } from "../../../../query/hooks/useFeatureFlags";
import { useQueryCustomerEntity } from "../../../../query/onboarding/queries/useQueryCustomerEntity";
import { useQueryMemberProfile } from "../../../../query/users/queries";
import { useBrowserPageTitle } from "../../../../shared/hooks/useBrowserPageTitle";
import { selectorUseInternationalPayouts } from "../../../../shared/selectors/selectorUseInternationalPayouts";
import { plural } from "../../../../shared/utils/plural";
import { useRedirectToByKeyPress } from "../../../../utils/useRedirectToByKeyPress";
import { PATH_PLATFORM_BALANCE } from "../../../PlatformBalance/paths";
import { AlertEnableTaxWithholdings } from "../../../Taxes/components/AlertEnableTaxWithholdings";
import { useCustomization } from "../../../customization";
import { DashboardTabs } from "../../components/DashboardTabs";
import { useQueryInvoiceTemplateRowsSummary } from "../../queries/useQueryInvoiceTemplateRowsSummary";
import { RouteDeposited } from "./routes/RouteDeposited";
import { RouteDrafts } from "./routes/RouteDrafts";
import { RouteInProgress } from "./routes/RouteInProgress";
import { RouteIndex } from "./routes/RouteIndex";
import { RouteOutstanding } from "./routes/RouteOutstanding";

export const RouteDashboard: React.FC = () => {
  const { terminology } = useCustomization();

  useBrowserPageTitle("Invoicing");
  const history = useHistory();
  const userId = useUserId();
  const featureFlags = useFeatureFlags();
  const queryInvoiceTemplateRowsSummary = useQueryInvoiceTemplateRowsSummary({
    filter: {
      status: {
        in: [
          FrequencyAndScheduleStatus.Active,
          FrequencyAndScheduleStatus.Draft
        ]
      }
    }
  });
  const queryMember = useQueryMemberProfile(userId);
  const queryCustomerEntity = useQueryCustomerEntity();

  const showManageSeries = useMemo(
    () => !!queryInvoiceTemplateRowsSummary.data?.listSize,
    [queryInvoiceTemplateRowsSummary.data?.listSize]
  );

  useRedirectToByKeyPress(KEYBOARD_SHORTCUT_SEARCH, `${INVOICING_PATH}/search`);

  const pageActions = useMemo(() => {
    const actions: WSPageToolbarAction[] = [
      {
        label: "Search",
        icon: "search",
        buttonKind: "Tertiary",
        onClick: () => history.push(`${INVOICING_PATH}/search`),
        hideButtonOn: false
      },
      {
        label: "Create invoice",
        icon: "plus",
        buttonKind: "Primary",
        onClick: () => history.push(INVOICING_PATH + "/create")
      }
    ];

    if (showManageSeries) {
      actions.unshift({
        buttonKind: "Tertiary",
        label: "Scheduled invoices",
        onClick: () => history.push(INVOICING_PATH + "/scheduled")
      });
    }

    actions.push({
      label: "Payouts (Beta)",
      onClick: () => history.push(INVOICING_PATH + "/payouts")
    });

    if (featureFlags.data?.platformBalance) {
      const isInternational = queryMember.data
        ? selectorUseInternationalPayouts(
            queryMember.data,
            queryCustomerEntity.data
          )
        : false;

      if (isInternational) {
        actions.push({
          label: "Platform balance",
          onClick: () => history.push(PATH_PLATFORM_BALANCE)
        });
      }
    }

    return actions;
  }, [
    featureFlags.data?.platformBalance,
    history,
    queryCustomerEntity.data,
    queryMember.data,
    showManageSeries
  ]);

  return (
    <WSPage>
      <WSPageToolbar
        title={plural(terminology().getPaidInvoice)}
        actions={pageActions}
      />

      <WSList gap="2XL">
        <AlertEnableTaxWithholdings />

        <WSElement>
          <DashboardTabs mb="L" />
          <Switch>
            <Route path={INVOICING_PATH + "/drafts"} component={RouteDrafts} />
            <Route
              path={INVOICING_PATH + "/outstanding"}
              component={RouteOutstanding}
            />
            <Route
              path={INVOICING_PATH + "/in-progress"}
              component={RouteInProgress}
            />
            <Route
              path={INVOICING_PATH + "/deposited"}
              component={RouteDeposited}
            />
            <Route path={INVOICING_PATH} component={RouteIndex} />
          </Switch>
        </WSElement>
      </WSList>
    </WSPage>
  );
};
